import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Cloud, Server, TrendingDown, ArrowRight } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { lenisRef } from "@/lib/lenis";
import { useI18n } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];

const C = {
  ro: {
    label: "Calculator economii",
    h1: "Cât te costă",
    hkw: "cloud-ul pe 3 ani?",
    sub: "Mută cursorul la cât plătești acum lunar în cloud. Îți arătăm cât ai da pe 3 ani vs. o infrastructură proprie, pe care o deții.",
    sliderLabel: "Plata ta lunară în cloud",
    sliderLabel2: "Sedii / utilizatori",
    cloud: "Cloud public · 3 ani",
    cloudNote: "abonament care curge la nesfârșit",
    infra: "Infrastructura ta · 3 ani",
    infraNote: "investiție o dată + mentenanță — o deții",
    save: "Economisești în 3 ani",
    savePct: "mai puțin",
    cta: "Trimite-mi estimarea pe email",
    disclaimer: "* Estimare orientativă. Cifra exactă depinde de proiect — îți dăm un buget clar și transparent după o scurtă discuție.",
    perMonth: "/lună",
    estSubject: "Estimare economii — cerere ofertă",
    estIntro: "Bună! Am folosit calculatorul de pe site și aș vrea o ofertă personalizată.",
    estMonthly: "Plata lunară actuală în cloud",
    estUnits: "Sedii / utilizatori",
    estCloud: "Cloud pe 3 ani (estimat)",
    estInfra: "Infrastructura mea pe 3 ani (estimat)",
    estSave: "Economie estimată pe 3 ani",
    estOutro: "Aștept o estimare exactă. Mulțumesc!",
    toastOk: "Estimarea a fost trecută în formular — completează datele tale și apasă „Trimite mesajul”.",
  },
  en: {
    label: "Savings calculator",
    h1: "How much does",
    hkw: "the cloud cost over 3 years?",
    sub: "Drag the slider to what you pay monthly in the cloud today. We'll show what you'd spend over 3 years vs. your own infrastructure that you own.",
    sliderLabel: "Your monthly cloud bill",
    sliderLabel2: "Sites / users",
    cloud: "Public cloud · 3 years",
    cloudNote: "a subscription that runs forever",
    infra: "Your infrastructure · 3 years",
    infraNote: "one-time investment + maintenance — you own it",
    save: "You save over 3 years",
    savePct: "less",
    cta: "Email me the estimate",
    disclaimer: "* Rough estimate. The exact figure depends on the project — we give you a clear, transparent budget after a short talk.",
    perMonth: "/mo",
    estSubject: "Savings estimate — quote request",
    estIntro: "Hi! I used the calculator on the site and I'd like a personalized quote.",
    estMonthly: "Current monthly cloud bill",
    estUnits: "Sites / users",
    estCloud: "Cloud over 3 years (estimated)",
    estInfra: "My infrastructure over 3 years (estimated)",
    estSave: "Estimated savings over 3 years",
    estOutro: "Looking forward to an exact estimate. Thanks!",
    toastOk: "The estimate is now in the form — fill in your details and hit \"Send message\".",
  },
};

export const CostCalculator = () => {
  const { lang } = useI18n();
  const isEn = lang === "en";
  const d = C[isEn ? "en" : "ro"];
  const [monthly, setMonthly] = useState(600);
  const [units, setUnits] = useState(8);

  const cloud3y = monthly * 36;
  const ratio = Math.max(9, 17.4 - (units - 1) * 0.18);
  const infra3y = Math.round(monthly * ratio);
  const savings = cloud3y - infra3y;
  const pct = Math.round((savings / cloud3y) * 100);
  const infraWidth = Math.max(12, Math.round((infra3y / cloud3y) * 100));

  const fmt = (n) => `${n.toLocaleString(isEn ? "en-US" : "ro-RO")} €`;

  const sendEstimate = () => {
    const message =
      `${d.estIntro}\n\n` +
      `${d.estMonthly}: ${fmt(monthly)}${d.perMonth}\n` +
      `${d.estUnits}: ${units}\n` +
      `${d.estCloud}: ${fmt(cloud3y)}\n` +
      `${d.estInfra}: ~${fmt(infra3y)}\n` +
      `${d.estSave}: ~${fmt(savings)} (−${pct}%)\n\n` +
      `${d.estOutro}`;
    window.dispatchEvent(
      new CustomEvent("dmo:prefill-contact", { detail: { subject: d.estSubject, message, source: "calculator" } })
    );
    const lenis = lenisRef.current;
    if (lenis) lenis.scrollTo("#contact", { offset: -60, duration: 1.2 });
    else document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
    toast.success(d.toastOk);
  };

  return (
    <section
      id="calculator"
      data-testid="cost-calculator-section"
      className="relative overflow-hidden border-t border-white/10 bg-ink-900"
    >
      <div className="absolute inset-0 grid-lines opacity-20" />
      <div className="pointer-events-none absolute -left-32 bottom-0 h-[26rem] w-[26rem] rounded-full bg-cyan-neon/10 blur-[150px]" />

      <div className="relative mx-auto max-w-5xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="max-w-3xl">
          <Reveal><SectionLabel index="[ CALCULATOR ]">{d.label}</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl">
              {d.h1} <span className="text-aurora text-aurora-glow">{d.hkw}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl font-body text-base leading-relaxed text-white/60">{d.sub}</p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="card-edge mt-12 rounded-2xl p-6 sm:p-10" data-testid="calc-card">
            {/* Slider */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <label htmlFor="calc-slider" className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/45">
                {d.sliderLabel}
              </label>
              <div className="font-display text-3xl font-extrabold text-cyan-neon" data-testid="calc-monthly">
                {fmt(monthly)}<span className="text-base font-normal text-white/40">{d.perMonth}</span>
              </div>
            </div>
            <input
              id="calc-slider"
              data-testid="calc-slider"
              type="range"
              min={100}
              max={5000}
              step={50}
              value={monthly}
              onChange={(e) => setMonthly(Number(e.target.value))}
              className="calc-range mt-4 w-full"
            />
            <div className="mt-1 flex justify-between font-mono text-[10px] text-white/30">
              <span>100 €</span><span>5.000 €</span>
            </div>

            {/* Slider 2 — units */}
            <div className="mt-8 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <label htmlFor="calc-slider-units" className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/45">
                {d.sliderLabel2}
              </label>
              <div className="font-display text-2xl font-extrabold text-white" data-testid="calc-units">{units}</div>
            </div>
            <input
              id="calc-slider-units"
              data-testid="calc-slider-units"
              type="range"
              min={1}
              max={50}
              step={1}
              value={units}
              onChange={(e) => setUnits(Number(e.target.value))}
              className="calc-range mt-4 w-full"
            />
            <div className="mt-1 flex justify-between font-mono text-[10px] text-white/30">
              <span>1</span><span>50</span>
            </div>

            {/* Bars */}
            <div className="mt-10 space-y-6">
              <div data-testid="calc-cloud">
                <div className="mb-2 flex items-center justify-between">
                  <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white/50">
                    <Cloud size={15} className="text-white/40" /> {d.cloud}
                  </span>
                  <span className="font-display text-xl font-bold text-white/70">{fmt(cloud3y)}</span>
                </div>
                <div className="h-3 w-full overflow-hidden rounded-full bg-white/5">
                  <div className="h-full w-full rounded-full bg-gradient-to-r from-white/25 to-white/40" />
                </div>
                <div className="mt-1 font-body text-[11px] text-white/35">{d.cloudNote}</div>
              </div>

              <div data-testid="calc-infra">
                <div className="mb-2 flex items-center justify-between">
                  <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-cyan-neon">
                    <Server size={15} /> {d.infra}
                  </span>
                  <span className="font-display text-xl font-bold text-cyan-neon">{fmt(infra3y)}</span>
                </div>
                <div className="h-3 w-full overflow-hidden rounded-full bg-white/5">
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-cyan-neon to-[#37b6ff] shadow-[0_0_20px_rgba(0,229,255,0.4)]"
                    animate={{ width: `${infraWidth}%` }}
                    transition={{ duration: 0.5, ease: EASE }}
                  />
                </div>
                <div className="mt-1 font-body text-[11px] text-cyan-neon/50">{d.infraNote}</div>
              </div>
            </div>

            {/* Savings */}
            <div className="mt-10 flex flex-col items-center gap-4 rounded-xl border border-cyan-neon/25 bg-cyan-neon/[0.06] p-6 sm:flex-row sm:justify-between">
              <div className="flex items-center gap-4">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl border border-cyan-neon/30 bg-cyan-neon/10 text-cyan-neon">
                  <TrendingDown size={22} />
                </span>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/45">{d.save}</div>
                  <div className="font-display text-3xl font-extrabold text-white sm:text-4xl" data-testid="calc-savings">
                    {fmt(savings)}
                    <span className="ml-2 align-middle font-mono text-sm font-bold text-cyan-neon">−{pct}% {d.savePct}</span>
                  </div>
                </div>
              </div>
              <button
                data-testid="calc-cta"
                onClick={sendEstimate}
                className="btn-shine group inline-flex shrink-0 items-center gap-2 bg-cyan-neon px-6 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.1em] text-black transition-colors duration-300 hover:bg-white"
              >
                {d.cta} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>

            <p className="mt-5 font-body text-[11px] leading-relaxed text-white/30">{d.disclaimer}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
